import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, IconButton, DialogContent, Button, Typography, TextField, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useCreateUserStore } from 'Application/store/useUserStore';
import { ExchangeRateParams } from 'Application/models';
import { checkIfInvalidRate, getKeyFromObjectValue } from 'Application/utils';
import { ThemeMode } from 'config';
import { useExchangeRateStore } from 'Application/store';
import { toast } from 'sonner';

interface EditExchangeRateProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    exchangeRate: ExchangeRateParams;
    setIsExchangeRatesChange: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditExchangeRate: React.FC<EditExchangeRateProps> = ({ isOpen, setIsOpen, exchangeRate, setIsExchangeRatesChange }) => {
    const { isLoading } = useCreateUserStore();
    const [formExchangeRate, setFormExchangeRate] = useState<ExchangeRateParams>(exchangeRate);
    const { fromCurrency, toCurrency, rate } = formExchangeRate;

    const fromCurrencyFieldName = getKeyFromObjectValue(formExchangeRate, fromCurrency);
    const toCurrencyFieldName = getKeyFromObjectValue(formExchangeRate, toCurrency);
    const rateFieldName = getKeyFromObjectValue(formExchangeRate, rate);

    // Set the formExchangeRate to the exchangeRate params when the user opens the modal.
    useEffect(() => {
        isOpen && setFormExchangeRate(exchangeRate);
    }, [isOpen]);

    const { updateExchangeRate } = useExchangeRateStore((state) => ({
        updateExchangeRate: state.updateExchangeRate
    }));

    const ifInvalidRate = checkIfInvalidRate(rate);

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const handleClose = () => {
        setIsOpen(false);
        setFormExchangeRate(exchangeRate); // Reset to the original exchange rate if modal is closed.
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = event.target;

        // Set rate to zero if rate is edited and value is equal to empty string
        if (name === 'rate' && value === '') {
            setFormExchangeRate({
                ...formExchangeRate,
                rate: 0
            });
            return;
        }

        const newValue = name === 'fromCurrency' || name === 'toCurrency' ? value : parseFloat(value) < 0 ? 0 : parseFloat(value);

        setFormExchangeRate({
            ...formExchangeRate,
            [name]: newValue
        });

        /* Used for removing leading zero from user's side when previous input value is just zero 
        (that is, from "02" to "2"). */
        event.target.value = name === 'rate' ? Number(value).toString() : value;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            await updateExchangeRate(fromCurrency, toCurrency, rate);
            setIsExchangeRatesChange(true);
            handleClose();
            toast.success('Exchange rate updated successfully!');
        } catch (error) {
            toast.error('Error updating exchange rate.');
            console.error(error);
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>
                        Edit {exchangeRate.fromCurrency} to {exchangeRate.toCurrency} exchange rate
                    </span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Provide the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            From
                        </Typography>
                        <TextField
                            placeholder="USD"
                            required
                            name={fromCurrencyFieldName}
                            variant="outlined"
                            type="text"
                            value={fromCurrency}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            To
                        </Typography>
                        <TextField
                            placeholder="KES"
                            required
                            name={toCurrencyFieldName}
                            variant="outlined"
                            type="text"
                            value={toCurrency}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Exchange Rate
                        </Typography>
                        <TextField
                            placeholder="131.23"
                            required
                            name={rateFieldName}
                            variant="outlined"
                            type="number"
                            value={rate}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>
                    {ifInvalidRate && (
                        <Typography style={{ color: '#ff0000' }}>Exchange rate should not have more than four decimal places.</Typography>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                            disabled={isLoading} // Disable button while loading
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={ifInvalidRate} // Disable button while loading
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditExchangeRate;
